import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { SECONDARY_CTA_TEXT } from '../../../../config/cta'
import LoopCTAs from '../../../../components/LoopCTAs'
import Seo from '../../../../components/Seo'

const exampleQuestions = [
  "I noticed you recently cancelled. I'd love to understand what drove that decision—there's no wrong answer.",
  "You mentioned [competitor]. What are they doing that we weren't?",
  'If we could have fixed one thing, what would have kept you?',
  'Looking back at your experience with us, what would you tell someone considering signing up?',
]

export default function ChurnAnalysis() {
  return (
    <div>
      <Seo
        title="Churn Analysis"
        description="Understand the real reasons customers leave. Post-churn conversational interviews that surface root causes, not just exit-survey checkboxes."
        path="/feedback-loops/customer/churn/analysis"
      />

      {/* Breadcrumbs */}
      <div className="bg-white border-b border-intelligence-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm text-intelligence-500">
            <Link to="/" className="hover:text-intelligence-900">
              Home
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/feedback-loops" className="hover:text-intelligence-900">
              Use Cases
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/feedback-loops/customer/churn" className="hover:text-intelligence-900">
              Churn Research
            </Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-intelligence-900">Analysis</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-intelligence-900 mb-4">
            Understand Why Customers Leave
          </h1>
          <p className="text-xl text-intelligence-600 max-w-3xl mx-auto mb-8">
            Don&apos;t guess. Ask—and actually understand.
          </p>
          <a
            href="https://rays.prismscope.ai/i/int_ONF-JNX1Zq0CyFEt0jzyGA?skip=1"
            className="inline-flex items-center justify-center px-8 py-4 text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg shadow-md transition-colors"
          >
            {SECONDARY_CTA_TEXT}
          </a>
        </div>
      </section>

      {/* The Problem */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-8 text-center">
            The Problem
          </h2>
          <div className="bg-white rounded-xl border border-intelligence-200 p-8 md:p-12">
            <p className="text-lg text-intelligence-700 mb-6">
              Customers are churning. You have theories:
            </p>
            <div className="bg-intelligence-50 rounded-lg p-6 mb-6">
              <ul className="space-y-2 text-intelligence-600">
                <li>• Maybe it&apos;s price</li>
                <li>• Maybe it&apos;s the competition</li>
                <li>• Maybe it&apos;s a feature gap</li>
              </ul>
            </div>
            <p className="text-lg text-intelligence-900 font-medium text-center">
              But theories aren&apos;t insights. And the customers who left aren&apos;t filling out
              your exit surveys.
            </p>
          </div>
        </div>
      </section>

      {/* How PrismScope Helps */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-8 text-center">
            How PrismScope Helps
          </h2>
          <div className="bg-accent-50 rounded-xl p-8 md:p-12">
            <p className="text-lg text-intelligence-700 mb-6">
              PrismScope catches customers at the moment they churn—or before.
            </p>
            <p className="text-lg text-accent-700 font-medium">
              Trigger a conversation when someone cancels, downgrades, or shows disengagement
              signals. Get the real story, not the polite excuse.
            </p>
          </div>
        </div>
      </section>

      {/* Example Questions */}
      <section className="bg-intelligence-50 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-intelligence-900 mb-12 text-center">
            Example Questions
          </h2>
          <div className="space-y-4">
            {exampleQuestions.map((question, index) => (
              <div key={index} className="bg-white rounded-lg border border-intelligence-200 p-6">
                <p className="text-intelligence-700 italic">&ldquo;{question}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-primary-600 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Stop guessing. Start understanding.
          </h2>
          <p className="text-lg text-primary-100 mb-8">
            Run your first campaign. Try a 3-minute conversation first.
          </p>
          <LoopCTAs location="feedback-loop-churn-analysis-final" align="center" />
        </div>
      </section>
    </div>
  )
}
